import { reportCustomEvent } from '@/utils/arms';
import { truncateArmsFailDetail } from '@/utils/arms/common';
import { formatTrackerMoney } from '@/utils/tracker/pay/recharge-result';

import type { TRechargeGoodsType } from '@/utils/tracker/pay/recharge-result';

export const ARMS_STRIPE_EVENT_TYPE = 'stripe' as const;
export const ARMS_STRIPE_EVENT_GROUP = 'commerce' as const;

const STRIPE_EVENT_NAME = {
    ATTEMPT: 'stripe_attempt',
    SUCCESS: 'stripe_success',
    FAIL: 'stripe_fail',
    JS_INIT: 'stripe_js_init',
    WALLET_PROBE: 'stripe_wallet_probe',
    PAYMENT_ERROR: 'stripe_payment_error',
} as const;

/** Stripe 出站渠道（perf 下钻） */
export type TStripeCheckoutChannel =
    | 'hosted_checkout'
    | 'payment_sheet_card'
    | 'payment_sheet_apple'
    | 'payment_sheet_google';

/** Stripe 支付失败原因（供 Grafana 告警与下钻） */
export type TStripeArmsFailReason =
    /** commerce/order/create/v1 失败 */
    | 'order_create_api_failed'
    /** Hosted Checkout 创单成功但缺少 checkout_url */
    | 'order_create_missing_checkout_url'
    /** 一次性创单成功但缺少 publishable_key / client_secret */
    | 'order_create_missing_payment_payload'
    /** Hosted Checkout 回跳 cancel（用户取消，未调 confirm） */
    | 'stripe_return_cancel'
    /** confirm 终态 FAILED */
    | 'order_confirm_failed'
    /** confirm 终态 CANCELED */
    | 'order_confirm_canceled'
    /** confirm 轮询结束仍为 PENDING */
    | 'order_confirm_pending_timeout'
    /** confirm 请求全程无成功响应 */
    | 'order_confirm_api_failed'
    /** Stripe.js loadStripe 失败 */
    | 'stripe_js_init_failed'
    /** confirmPayment 返回 error（非用户关闭） */
    | 'stripe_confirm_payment_failed'
    /** wallet confirmPayment 非 card 错误走取消 */
    | 'stripe_confirm_payment_canceled'
    /** 用户关闭 Payment Sheet 弹窗 / 点 mask */
    | 'stripe_sheet_dismiss_cancel'
    /** ExpressCheckout elements.submit 失败 */
    | 'stripe_wallet_submit_cancel'
    /** confirmPayment 成功但无 paymentIntent.id */
    | 'stripe_missing_payment_intent';

export interface IStripeArmsTrackingProps {
    /** reader | pay */
    return_scene: string;
    goods_type: TRechargeGoodsType;
    gear_id: string;
    trigger_scene: string;
    /** card | apple | google */
    payment_method: string;
    paywall_template_id?: string;
    book_id?: string;
    chapter_id?: string;
    /** 最小货币单位（如美分），上报时转为美元小数 */
    money?: number;
    checkout_channel?: TStripeCheckoutChannel;
}

export interface IReportStripeSuccessProps extends IStripeArmsTrackingProps {
    order_id: string;
}

export interface IReportStripeFailProps extends IStripeArmsTrackingProps {
    fail_reason: TStripeArmsFailReason;
    fail_detail?: string;
    order_id?: string;
}

/** 创单 / 出站失败时传给 onError，仅用于 ARMS */
export interface IStripeCheckoutArmsFailContext {
    fail_reason: TStripeArmsFailReason;
    order_id?: string;
    fail_detail?: string;
}

export interface IReportStripeJsInitProps {
    success: 0 | 1;
    duration_ms: number;
    fail_detail?: string;
}

export interface IReportStripeWalletProbeProps {
    apple_available: 0 | 1;
    google_available: 0 | 1;
    duration_ms: number;
}

export interface IReportStripePaymentErrorProps {
    stage: 'confirm_payment' | 'elements_submit';
    order_id?: string;
    payment_method: string;
    error_code?: string;
    fail_detail?: string;
}

function toArmsProperties(props: Record<string, string | number>): Record<string, string | number> {
    return {
        ...props,
        trigger_scene: props.trigger_scene || '',
        return_scene: props.return_scene || '',
        payment_method: props.payment_method || '',
    };
}

function sendStripeArmsEvent(name: string, value: number, properties: Record<string, string | number>): void {
    reportCustomEvent(name, ARMS_STRIPE_EVENT_TYPE, {
        group: ARMS_STRIPE_EVENT_GROUP,
        value,
        properties: toArmsProperties(properties),
    });
}

function buildStripeArmsPayload(props: IStripeArmsTrackingProps): Record<string, string | number> {
    const payload: Record<string, string | number> = {
        return_scene: props.return_scene,
        goods_type: props.goods_type,
        gear_id: props.gear_id,
        trigger_scene: props.trigger_scene,
        payment_method: props.payment_method,
        paywall_template_id: props.paywall_template_id ?? '',
        book_id: props.book_id ?? '',
        chapter_id: props.chapter_id ?? '',
    };
    if (props.money !== undefined) {
        payload.money = formatTrackerMoney(props.money);
    }
    if (props.checkout_channel) {
        payload.checkout_channel = props.checkout_channel;
    }
    return payload;
}

/** 用户发起 Stripe 支付（创单前，成功率分母） */
export function reportStripeAttempt(props: IStripeArmsTrackingProps): void {
    sendStripeArmsEvent(STRIPE_EVENT_NAME.ATTEMPT, 1, buildStripeArmsPayload(props));
}

/** confirm 终态 PAID */
export function reportStripeSuccess(props: IReportStripeSuccessProps): void {
    sendStripeArmsEvent(STRIPE_EVENT_NAME.SUCCESS, 1, {
        ...buildStripeArmsPayload(props),
        order_id: props.order_id,
        result_status: 'success',
    });
}

/** 支付失败 */
export function reportStripeFail(props: IReportStripeFailProps): void {
    const payload: Record<string, string | number> = {
        ...buildStripeArmsPayload(props),
        result_status: 'fail',
        fail_reason: props.fail_reason,
        ...(props.order_id ? { order_id: props.order_id } : {}),
        ...(props.fail_detail ? { fail_detail: truncateArmsFailDetail(props.fail_detail) } : {}),
    };
    sendStripeArmsEvent(STRIPE_EVENT_NAME.FAIL, 0, payload);
}

/** Stripe.js 预加载结果 */
export function reportStripeJsInit(props: IReportStripeJsInitProps): void {
    const payload: Record<string, string | number> = {
        success: props.success,
        duration_ms: props.duration_ms,
        ...(props.fail_detail ? { fail_detail: truncateArmsFailDetail(props.fail_detail) } : {}),
    };
    sendStripeArmsEvent(STRIPE_EVENT_NAME.JS_INIT, props.success, payload);
}

/** Apple / Google Pay 可用性探测结果 */
export function reportStripeWalletProbe(props: IReportStripeWalletProbeProps): void {
    sendStripeArmsEvent(STRIPE_EVENT_NAME.WALLET_PROBE, 1, { ...props });
}

/** confirmPayment / elements 错误（诊断用，可能与 stripe_fail 同时出现） */
export function reportStripePaymentError(props: IReportStripePaymentErrorProps): void {
    const payload: Record<string, string | number> = {
        stage: props.stage,
        payment_method: props.payment_method,
        ...(props.order_id ? { order_id: props.order_id } : {}),
        ...(props.error_code ? { error_code: props.error_code } : {}),
        ...(props.fail_detail ? { fail_detail: truncateArmsFailDetail(props.fail_detail) } : {}),
    };
    sendStripeArmsEvent(STRIPE_EVENT_NAME.PAYMENT_ERROR, 0, payload);
}

export function mapConfirmStatusToStripeFailReason(status: string): TStripeArmsFailReason {
    if (status === 'FAILED') {
        return 'order_confirm_failed';
    }
    if (status === 'CANCELED') {
        return 'order_confirm_canceled';
    }
    if (status === 'PENDING') {
        return 'order_confirm_pending_timeout';
    }
    return 'order_confirm_failed';
}

export function formatStripePaymentErrorDetail(err: { type?: string; code?: string; message?: string }): string {
    const parts: string[] = [];
    if (err.type) {
        parts.push(`type:${err.type}`);
    }
    if (err.code) {
        parts.push(`code:${err.code}`);
    }
    if (err.message) {
        parts.push(`message:${err.message}`);
    }
    return parts.join(';');
}
